/**
 * État d'authentification du bureau.
 *
 * Trois états seulement, pour que l'interface n'ait jamais à deviner :
 * `chargement` (on tente de reprendre une session), `anonyme`, `connecté`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  api,
  getRefreshToken,
  onSessionLost,
  setAccessToken,
  setRefreshToken,
  type LoginResult,
} from '../api/client';
import { sessionTenueMalgreRefus } from '../domaine/session';

export type AuthState =
  | { readonly kind: 'chargement' }
  | { readonly kind: 'anonyme' }
  | { readonly kind: 'connecte'; readonly email: string };

interface AuthContextValue {
  readonly state: AuthState;
  /** Retourne `'MFA_REQUIRED'`, `'MFA_SETUP_REQUIRED'`, ou `'AUTHENTICATED'`. */
  readonly login: (email: string, password: string) => Promise<LoginResult>;
  readonly verifyMfa: (
    challenge: string,
    code: string,
    email: string,
    seSouvenirAppareil: boolean,
  ) => Promise<void>;
  readonly confirmMfaSetup: (
    challenge: string,
    code: string,
    email: string,
    seSouvenirAppareil: boolean,
  ) => Promise<void>;
  readonly logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Mémorise l'adresse pour l'afficher après reprise de session. */
const EMAIL_KEY = 'air.email';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: 'chargement' });

  const applySession = useCallback((result: LoginResult, email: string) => {
    if (result.status !== 'AUTHENTICATED') {
      return;
    }
    setAccessToken(result.accessToken);
    setRefreshToken(result.refreshToken);
    try {
      window.localStorage.setItem(EMAIL_KEY, email);
    } catch {
      /* stockage indisponible : sans conséquence sur la session */
    }
    setState({ kind: 'connecte', email });
  }, []);

  // Reprise de session au chargement : on ne redemande la double
  // authentification que si le rafraîchissement échoue.
  useEffect(() => {
    let annule = false;

    const reprendre = async () => {
      const refresh = getRefreshToken();
      if (!refresh) {
        if (!annule) setState({ kind: 'anonyme' });
        return;
      }
      try {
        // `request` gère lui-même le rafraîchissement ; un appel anodin suffit
        // à savoir si la session tient encore.
        await api.onboarding();
        const email = window.localStorage.getItem(EMAIL_KEY) ?? '';
        if (!annule) setState({ kind: 'connecte', email });
      } catch (e) {
        // Un refus de droit (403) n'est pas une session perdue : cet appel exige un droit que
        // seuls l'administrateur et la comptabilité détiennent, et recharger la page
        // déconnectait tous les autres rôles.
        if (sessionTenueMalgreRefus(e)) {
          const email = window.localStorage.getItem(EMAIL_KEY) ?? '';
          if (!annule) setState({ kind: 'connecte', email });
          return;
        }
        setAccessToken(null);
        setRefreshToken(null);
        if (!annule) setState({ kind: 'anonyme' });
      }
    };

    void reprendre();
    return () => {
      annule = true;
    };
  }, []);

  // L'API signale une session définitivement perdue : on revient au login sans
  // attendre que l'utilisateur clique quelque part.
  useEffect(() => {
    onSessionLost(() => setState({ kind: 'anonyme' }));
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.login(email, password);
      applySession(result, email);
      return result;
    },
    [applySession],
  );

  const verifyMfa = useCallback(
    async (challenge: string, code: string, email: string, seSouvenirAppareil: boolean) => {
      const result = await api.verifyMfa(challenge, code, seSouvenirAppareil);
      applySession(result, email);
    },
    [applySession],
  );

  const confirmMfaSetup = useCallback(
    async (challenge: string, code: string, email: string, seSouvenirAppareil: boolean) => {
      const result = await api.confirmMfaSetup(challenge, code, seSouvenirAppareil);
      applySession(result, email);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    const refresh = getRefreshToken();
    if (refresh) {
      try {
        await api.logout(refresh);
      } catch {
        // Le serveur a peut-être déjà révoqué la famille : on efface quand même
        // côté client plutôt que de laisser l'utilisateur coincé.
      }
    }
    setAccessToken(null);
    setRefreshToken(null);
    setState({ kind: 'anonyme' });
  }, []);

  const value = useMemo(
    () => ({ state, login, verifyMfa, confirmMfaSetup, logout }),
    [state, login, verifyMfa, confirmMfaSetup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth doit être utilisé à l’intérieur de <AuthProvider>.');
  }
  return ctx;
}
